

import React, { useMemo, useEffect, useState } from 'react';
import { useGoalsData, useGoalLinkageData } from '../hooks/usePerformanceData';
import { useDataEngine } from '@dhis2/app-runtime';
import { Card, Button, Tooltip, colors } from '@dhis2/ui';
import { resolveActiveFiscalYear, getFallbackFiscalYear } from '../../GoalsManagement/utils/fiscalYearHelpers';
import StatusBadge from './StatusBadge';
import { BiBuildingHouse } from 'react-icons/bi';

// ProgressBar component
const ProgressBar = ({ value, height = '6px', borderRadius = '3px' }) => (
  <div style={{
    width: '100%',
    height,
    backgroundColor: '#e5e7eb',
    borderRadius
  }}>
    <div style={{
      width: `${Math.min(Math.max(value || 0, 0), 100)}%`,
      height: '100%',
      backgroundColor: getProgressColor(value || 0),
      borderRadius,
      transition: 'width 0.3s ease'
    }} />
  </div>
);

const getProgressColor = (progress) => {
  if (progress >= 80) return '#28a745';
  if (progress >= 50) return '#ffc107';
  return '#dc3545';
};

const getProgressDescription = (progress) => {
  if (progress >= 80) return 'Excellent';
  if (progress >= 50) return 'Good';
  if (progress >= 25) return 'Needs Attention';
  return 'Critical';
};

const calculateGoalStatus = (progress, startDate, endDate) => {
  const now = new Date();
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  if (progress >= 100) {
    return 'Completed';
  }

  // Case: Goal hasn't started yet
  if (start && now < start) {
    return 'Not Started';
  }

  // Case: Goal is overdue and not completed → highest priority
  if (end && now > end) {
    return 'Behind Schedule';
  }

  // Case: Goal is active — compute based on time vs progress
  if (start && end) {
    const totalDuration = end - start;
    const elapsed = now - start;

    if (totalDuration > 0) {
      const expectedProgress = (elapsed / totalDuration) * 100;

      if (progress >= expectedProgress + 20) return 'Ahead of Schedule';
      if (progress >= expectedProgress - 10) return 'On Track';
      if (progress >= expectedProgress - 30) return 'At Risk';
      return 'Behind Schedule';
    }
  }

  // Fallback: use progress only (no dates)
  if (progress >= 80) return 'On Track';
  if (progress >= 50) return 'At Risk';
  return 'Behind Schedule';
};

const RealGoals = ({ orgUnitId, performanceData, leadershipData, accessibleOrgUnitIds = [], filterScope, selectedFiscalYear = null, currentFilter = 'current' }) => {
  console.log('🎯 Current orgUnitId:', orgUnitId);
  console.log('👥 Leadership Data:', leadershipData);
  console.log('📋 Accessible Org Unit IDs:', accessibleOrgUnitIds);

  const engine = useDataEngine();
  const [fiscalYear, setFiscalYear] = useState({ start: '', end: '' });
  const [fiscalYearLoaded, setFiscalYearLoaded] = useState(false);
  const [showLeadershipInsights, setShowLeadershipInsights] = useState(true);

  const [userAccessibleOrgUnits, setUserAccessibleOrgUnits] = useState([]);


    // ✅ Fetch user's accessible org units
    useEffect(() => {
      const fetchUserOrgUnits = async () => {
        try {
          const { me } = await engine.query({
            me: {
              resource: 'me',
              params: { fields: 'organisationUnits[id,name,level]' }
            }
          });
          
          if (me.organisationUnits && me.organisationUnits.length > 0) {
            const orgUnitIds = me.organisationUnits.map(ou => ou.id);
            console.log('✅ User accessible org units:', orgUnitIds);
            setUserAccessibleOrgUnits(orgUnitIds);
          }
        } catch (error) {
          console.error('❌ Failed to fetch user org units:', error);
          // Fallback to provided accessibleOrgUnitIds
          setUserAccessibleOrgUnits(accessibleOrgUnitIds);
        }
      };
      
      if (engine) {
        fetchUserOrgUnits();
      }
    }, [engine, accessibleOrgUnitIds]);
  
  const accessibleOrgUnitsToUse = userAccessibleOrgUnits.length > 0 
    ? userAccessibleOrgUnits 
    : accessibleOrgUnitIds.length > 0 
      ? accessibleOrgUnitIds 
      : [orgUnitId];

  // Create a combined org unit filter string
  const orgUnitFilter = accessibleOrgUnitsToUse.join(';');

  // Only fetch goals and linkage data, use performanceData from props for objectives/tasks
  const { loading: goalsLoading, error: goalsError, data: goalsData } = useGoalsData(orgUnitId);
  const { loading: linkageLoading, error: linkageError, data: linkageData } = useGoalLinkageData(orgUnitId);

  const filteredGoals = React.useMemo(() => {
    const hasSharedGoals = Array.isArray(performanceData?.goals?.events);
    const companyGoals = hasSharedGoals
      ? performanceData.goals.events
      : (goalsData?.goals?.events || []);

    // Goals remain visible across scopes; scoped progress comes from linked
    // objectives/tasks that are already filtered upstream.
    return companyGoals;
  }, [performanceData, goalsData]);

  // ===== ENHANCED LEADERSHIP METRICS =====
  const leadershipMetrics = useMemo(() => {
    if (!leadershipData?.competencies?.length) {
      return {
        hasLeadershipData: false,
        averageScore: 0,
        completionRate: 0,
        teamAlignment: 0,
        pendingApprovals: 0,
        topCompetencies: [],
        needsAttention: []
      };
    }

    const competencies = leadershipData.competencies;
    const directReports = leadershipData.directReportAssessments || [];
    
    // Calculate average leadership score
    const scores = competencies.map(c => {
      const filledRatings = c.ratings.filter(r => r != null);
      if (!filledRatings.length) return 0;
      const avg = filledRatings.reduce((a, b) => a + b, 0) / filledRatings.length;
      return (c.weight || 0) * avg;
    });
    const averageScore = scores.reduce((sum, s) => sum + s, 0) / (scores.length || 1);

    // Calculate completion rate (both periods rated)
    const completedCompetencies = competencies.filter(c => 
      c.ratings[0] !== null && c.ratings[1] !== null
    ).length;
    const completionRate = (completedCompetencies / (competencies.length || 1)) * 100;

    // Team alignment score (how well leadership scores align with goals progress)
    const teamAlignment = calculateTeamAlignment(performanceData, competencies);

    // Pending approvals count
    const pendingApprovals = directReports.reduce((count, report) => {
      const pending = report.competencies?.filter(c => 
        !c.approvalStatus || c.approvalStatus === 'pending'
      ).length || 0;
      return count + pending;
    }, 0);

    // Top performing competencies
    const topCompetencies = [...competencies]
      .filter(c => c.ratings.some(r => r !== null))
      .map(c => ({
        name: c.name,
        score: calculateCompetencyScore(c)
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    // Competencies needing attention (low scores)
    const needsAttention = competencies
      .filter(c => {
        const score = calculateCompetencyScore(c);
        return score < 2.5 && c.ratings.some(r => r !== null);
      })
      .map(c => ({
        name: c.name,
        score: calculateCompetencyScore(c)
      }));

    return {
      hasLeadershipData: true,
      averageScore: Math.round(averageScore * 10) / 10,
      completionRate: Math.round(completionRate),
      teamAlignment: Math.round(teamAlignment),
      pendingApprovals,
      topCompetencies,
      needsAttention,
      totalDirectReports: directReports.length,
      selfAssessmentComplete: completionRate === 100
    };
  }, [leadershipData, performanceData]);

  // Helper to calculate competency score
  const calculateCompetencyScore = (competency) => {
    const filledRatings = competency.ratings.filter(r => r != null);
    if (!filledRatings.length) return 0;
    const avg = filledRatings.reduce((a, b) => a + b, 0) / filledRatings.length;
    return avg;
  };

  // Calculate team alignment between leadership and goals
  const calculateTeamAlignment = (perfData, competencies) => {
    if (!perfData?.tasks?.events?.length || !competencies?.length) return 0;
    
    const taskCompletion = perfData.tasks.events.reduce((sum, task) => {
      const comp = parseInt(task.dataValues?.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value) || 0;
      return sum + comp;
    }, 0) / perfData.tasks.events.length;

    const leadershipScore = competencies.reduce((sum, c) => {
      const score = calculateCompetencyScore(c);
      return sum + score;
    }, 0) / (competencies.length * 5); // Normalize to 0-1

    // Calculate alignment (how well leadership score correlates with task completion)
    return Math.min(100, (leadershipScore * 100 + taskCompletion) / 2);
  };

  const globalTaskProgress = useMemo(() => {
    if (!performanceData?.tasks?.events?.length) return 0;
    const total = performanceData.tasks.events.reduce((sum, task) => {
      const comp = parseInt(task.dataValues?.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value) || 0;
      return sum + comp;
    }, 0);
    return Math.round(total / performanceData.tasks.events.length);
  }, [performanceData]);

  // Fiscal Year Loading Effect
  useEffect(() => {
    if (!orgUnitId || !engine) {
      // Fallback to current year if no orgUnitId
      const now = new Date();
      const year = now.getFullYear();
      setFiscalYear({ start: `${year}-01-01`, end: `${year}-12-31` });
      setFiscalYearLoaded(true);
      return;
    }

    const loadFiscalYear = async () => {
        try {
          // Try each accessible org unit until we find one with fiscal year data
          let foundFiscalYear = false;
          
          for (const orgUnit of accessibleOrgUnitsToUse) {
            try {
              // Get Staff TEI from enrollments
              const enrollmentsResponse = await engine.query({
                enrollmentsQuery: {
                  resource: 'enrollments',
                  params: {
                    ou: orgUnit, // ✅ Use current org unit from loop
                    program: 'NkReudUAW4F', // Company Goals Management
                    fields: 'trackedEntityInstance',
                    pageSize: 1,
                    order: 'lastUpdated:desc'
                  }
                }
              });
              
              const teiId = enrollmentsResponse.enrollmentsQuery?.enrollments?.[0]?.trackedEntityInstance;
              if (!teiId) {
                console.warn(`⚠️ No Staff TEI found for fiscal year in org unit ${orgUnit}`);
                continue;
              }
              
              // Query metadata event
              const eventsResponse = await engine.query({
                eventsQuery: {
                  resource: 'events',
                  params: {
                    program: 'NkReudUAW4F',
                    programStage: 'htIWgNQWEMm', // Company Metadata Setup
                    ou: orgUnit,
                    trackedEntityInstance: teiId,
                    pageSize: 1,
                    order: 'eventDate:desc',
                    fields: 'dataValues[dataElement,value]'
                  }
                }
              });
              
              const event = eventsResponse.eventsQuery?.events?.[0];
              let start = '', end = '';
              
              if (event) {
                event.dataValues?.forEach(dv => {
                  if (dv.dataElement === 'U55MqwBcRAe') start = dv.value; // FIN_START
                  if (dv.dataElement === 'kgdk4NXu4f5') end = dv.value;   // FIN_END
                });
              }

              // Resolve active fiscal year from pattern
              let resolvedFy;
              if (start && end) {
                resolvedFy = resolveActiveFiscalYear({ start, end });
                setFiscalYear(resolvedFy);
                console.log('✅ Fiscal year loaded from metadata:', resolvedFy);
                foundFiscalYear = true;
                break;
              } else {
                console.warn(`⚠️ Incomplete fiscal year data in org unit ${orgUnit}`);
                continue;
              }
            } catch (orgUnitError) {
              console.warn(`⚠️ No fiscal year in org unit ${orgUnit}:`, orgUnitError.message);
              continue;
            }
          }
          
          if (!foundFiscalYear) {
            // Fallback to current year
            const now = new Date();
            const year = now.getFullYear();
            setFiscalYear({ start: `${year}-01-01`, end: `${year}-12-31` });
            console.log('⚠️ Using fallback fiscal year (current year)');
          }
          
          setFiscalYearLoaded(true);
        } catch (error) {
          console.error('❌ Failed to load fiscal year:', error);
          // Safe fallback
          setFiscalYear(getFallbackFiscalYear());
          setFiscalYearLoaded(true);
        }
      };
    
    loadFiscalYear();
  }, [orgUnitId, engine]);

  React.useEffect(() => {
    if (goalsError) console.error('❌ Goals Data Error:', goalsError);
    if (linkageError) console.error('❌ Linkage Data Error:', linkageError);
  }, [goalsError, linkageError]);

  const isLoading = goalsLoading || linkageLoading || !fiscalYearLoaded;
  const error = goalsError || linkageError;

  // Process Goals (simplified version - you would include your full processing logic here)
  const processedGoals = useMemo(() => {
    const fiscal = selectedFiscalYear || fiscalYear;
    const fiscalStart = fiscal.start ? new Date(fiscal.start) : null;
    const fiscalEnd = fiscal.end ? new Date(fiscal.end) : null;

    // Access the raw API response structure with proper null checks
    const goalsEvents = filteredGoals || [];
    const objectivesEvents = performanceData?.objectives?.events || [];
    const linkageEvents = linkageData?.goalLinkage?.events || [];
    const tasksEvents = performanceData?.tasks?.events || [];

    console.log('🔍 RAW DATA STRUCTURE:');
    console.log('- goalsData:', goalsData);
    console.log('- performanceData (from props):', performanceData);
    console.log('- linkageData:', linkageData);
    console.log('📊 Events - Goals:', goalsEvents.length, 'Objectives:', objectivesEvents.length, 'Linkage:', linkageEvents.length, 'Tasks:', tasksEvents.length);

    if (!goalsEvents.length) {
      console.log('❌ NO GOALS FOUND');
      return [];
    }

    // Step 1: Build set of valid Goal IDs
    const validGoalIds = new Set(goalsEvents.map(g => g.event));
    console.log('✅ VALID GOAL IDs:', Array.from(validGoalIds));

    const linkageMap = {};
    if (linkageEvents.length > 0) {
      console.log('🔗 Processing linkage events:', linkageEvents.length);
      linkageEvents.forEach(event => {
        const dataValues = event.dataValues || [];
        
        const goalId = dataValues.find(dv =>
          dv.dataElement === 'HJqqfzhH614' || 
          dv.dataElement === 'GOAL_ID_FIELD' || 
          dv.dataElement === 'uBPW95V2VoX' 
        )?.value;

        const objectiveId = dataValues.find(dv =>
          dv.dataElement === 'EuN5ztFnXsQ' || 
          dv.dataElement === 'plJovUlxfQ0' || 
          dv.dataElement === 'neuilg3L4d2' || 
          dv.dataElement === 'VhrIQQIVpB9'
        )?.value;

        console.log(`🔗 Linkage check - Goal: ${goalId}, Objective: ${objectiveId}, Valid: ${goalId && validGoalIds.has(goalId)}`);

        if (goalId && objectiveId && validGoalIds.has(goalId)) {
          linkageMap[objectiveId] = goalId;
          console.log(`✅ Created linkage: Objective ${objectiveId} -> Goal ${goalId}`);
        }
      });
    }

    console.log('🔗 Final linkage map:', linkageMap);

    // Approach 2: Use objective alignment field (VhrIQQIVpB9)
    const objectiveAlignmentMap = {};
    if (performanceData?.objectives?.events) {
      performanceData.objectives.events.forEach(event => {
        const dataValues = event.dataValues || [];
        const objectiveId = event.event;
        const alignmentId = dataValues.find(dv => dv?.dataElement === 'VhrIQQIVpB9')?.value;
        if (alignmentId) {
          objectiveAlignmentMap[objectiveId] = alignmentId;
        }
      });
    }
    console.log('🔗 Objective alignment map:', objectiveAlignmentMap);

    // Function to find goal for an objective
    const getGoalIdForObjective = (objectiveId) => {
      // First try direct linkage
      if (linkageMap[objectiveId] && validGoalIds.has(linkageMap[objectiveId])) {
        return linkageMap[objectiveId];
      }

      // Then try alignment field
      const alignmentId = objectiveAlignmentMap[objectiveId];
      if (alignmentId && validGoalIds.has(alignmentId)) {
        return alignmentId;
      }

      return null;
    };

    // Step 3: Build objective to goal mapping and task progress per objective
    const objectivesByGoalId = {};
    const objectiveTasksMap = {};

    // First, group tasks by objective
    if (performanceData?.tasks?.events) {
      performanceData.tasks.events.forEach(task => {
        const dataValues = task.dataValues || [];
        const objectiveId = dataValues.find(dv => dv?.dataElement === 'plJovUlxfQ0')?.value;
        const completion = parseInt(dataValues.find(dv => dv?.dataElement === 'AElpNxzTdRh')?.value) || 0;

        if (objectiveId) {
          if (!objectiveTasksMap[objectiveId]) {
            objectiveTasksMap[objectiveId] = [];
          }
          objectiveTasksMap[objectiveId].push({
            completion,
            taskId: task.event
          });
        }
      });
    }

    console.log('🔍 Objective tasks map:', objectiveTasksMap);

    // Step 4: Group objectives by Goal ID
    let linkedCount = 0;

    if (performanceData?.objectives?.events) {
      console.log('🎯 Processing objectives, total:', performanceData.objectives.events.length);

      performanceData.objectives.events.forEach(event => {
        const dataValues = event.dataValues || [];
        const objectiveId = event.event;
        const linkedGoalId = getGoalIdForObjective(objectiveId);

        console.log(`🔗 Objective ${objectiveId} -> Goal: ${linkedGoalId}, valid: ${validGoalIds.has(linkedGoalId)}`);

        if (!linkedGoalId || !validGoalIds.has(linkedGoalId)) {
          console.log(`⚠️ Objective ${objectiveId} has no valid goal linkage`);
          return;
        }

        linkedCount++;
        const name = dataValues.find(dv => dv?.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed Objective';

        // Calculate progress for THIS specific objective based on ITS tasks
        const taskData = objectiveTasksMap[objectiveId] || [];
        let progress = 0;
        let taskCount = taskData.length;
        let completedTasks = 0;

        if (taskData.length > 0) {
          const totalCompletion = taskData.reduce((sum, task) => sum + task.completion, 0);
          progress = Math.round(totalCompletion / taskData.length);
          completedTasks = taskData.filter(task => task.completion === 100).length;
        }

        const objective = {
          id: objectiveId,
          name,
          progress,
          taskCount,
          completedTasks
        };

        if (!objectivesByGoalId[linkedGoalId]) {
          objectivesByGoalId[linkedGoalId] = [];
        }
        objectivesByGoalId[linkedGoalId].push(objective);
      });
    }

    console.log(`📊 Successfully linked ${linkedCount} objectives to goals`);

    // Step 5: Build goals with calculated progress
    return goalsEvents.map(event => {
      const dataValues = event.dataValues || [];
      const extract = (de) => dataValues.find(dv => dv?.dataElement === de)?.value || '';

      const goalName = extract('HJqqfzhH614') || 'Unnamed Goal';
      const goalStartStr = extract('MH7DQVegD1j');
      const goalEndStr = extract('pUvJQ9vy3A3');

      const effectiveStartDate = goalStartStr 
        ? new Date(goalStartStr) 
        : fiscalStart;
      const effectiveEndDate = goalEndStr 
        ? new Date(goalEndStr) 
        : fiscalEnd;

      const linkedObjectives = objectivesByGoalId[event.event] || [];

      // Calculate overall task progress from all linked objectives
      let overallTaskProgress = 0;
      let totalTasks = 0;
      let completedTasks = 0;
      let taskProgressSum = 0;
      let taskCount = 0;

      // Calculate actual task-based progress
      linkedObjectives.forEach(obj => {
        const tasks = objectiveTasksMap[obj.id] || [];
        totalTasks += tasks.length;
        completedTasks += tasks.filter(task => task.completion === 100).length;
        
        // Sum task progress for averaging
        tasks.forEach(task => {
          taskProgressSum += task.completion;
          taskCount++;
        });
      });

      // Calculate overall progress based on task completions
      if (taskCount > 0) {
        overallTaskProgress = Math.round(taskProgressSum / taskCount);
      }

      console.log(`🎯 Goal "${goalName}": ${totalTasks} tasks, ${completedTasks} completed, Overall Progress: ${overallTaskProgress}%`);

      // Direct goal progress (if entered manually)
      const directProgress = parseInt(extract('pOOGDRzEo98')) || 0;

      let progress = directProgress;
      let progressSource = 'direct';
      let calculatedProgress = 0;

      // Calculate progress from linked objectives if we have them
      if (linkedObjectives.length > 0) {
        calculatedProgress = Math.round(
          linkedObjectives.reduce((sum, obj) => sum + obj.progress, 0) / linkedObjectives.length
        );
        
        // Use calculated progress if it's more meaningful than direct progress
        if (directProgress === 0 || calculatedProgress > directProgress || taskCount > 0) {
          progress = Math.max(directProgress, calculatedProgress, overallTaskProgress);
          progressSource = 'calculated';
        }
      }

      // If we have actual tasks, use their average progress
      if (taskCount > 0 && overallTaskProgress > progress) {
        progress = overallTaskProgress;
        progressSource = 'tasks';
      }

      const hasActivity = linkedObjectives.length > 0 && 
                  linkedObjectives.some(obj => obj.taskCount > 0);

      if (!hasActivity && directProgress === 0) {
        progress = 0;
      }

      let effectiveProgressForStatus = progress;
      if (progress >= 100 && totalTasks > 0 && completedTasks < totalTasks) {
        // If tasks exist but not all are done, use task progress for status
        effectiveProgressForStatus = overallTaskProgress;
      }

      let status = calculateGoalStatus(effectiveProgressForStatus, effectiveStartDate, effectiveEndDate);

      // Still check for "Completed" override
      if (status === 'Completed' && totalTasks > 0 && completedTasks < totalTasks) {
        // If status says completed but tasks aren't all done, recalculate
        status = calculateGoalStatus(overallTaskProgress, effectiveStartDate, effectiveEndDate);
      }

      // Override: Never mark as "Completed" if there's no real activity
      if (progress >= 100 && !hasActivity) {
        status = 'Not Started';
      }

      return {
        id: event.event,
        name: goalName,
        description: extract('zS8QIgZc0Ll'),
        progress,
        directProgress,
        calculatedProgress,
        overallProgress: overallTaskProgress,
        progressSource,
        status,
        eventDate: event.eventDate,
        startDate: goalStartStr,         
        endDate: goalEndStr, 
        priority: extract('Co2i5BleCcZ') || 'medium',
        category: extract('uBPW95V2VoX'),
        linkedObjectives,
        totalTasks,
        completedTasks,
      };
    });
  }, [goalsData, performanceData, linkageData, fiscalYear, selectedFiscalYear]);

  const filteredGoalsByFY = useMemo(() => {
    if (Array.isArray(performanceData?.goals?.events)) return processedGoals;
    if (currentFilter === 'all') return processedGoals;

    const fiscalYearToUse = selectedFiscalYear || fiscalYear;
    if (!fiscalYearToUse?.start || !fiscalYearToUse?.end) return processedGoals;

    const fyStart = new Date(fiscalYearToUse.start);
    const fyEnd = new Date(fiscalYearToUse.end);
    fyEnd.setHours(23, 59, 59, 999);

    return processedGoals.filter(goal => {
        // Try eventDate first (most reliable for DHIS2 events)
        if (goal.eventDate) {
            const d = new Date(goal.eventDate);
            return d >= fyStart && d <= fyEnd;
        }
        // Try startDate and endDate, allowing goals that overlap the FY
        if (goal.startDate && goal.endDate) {
            const start = new Date(goal.startDate);
            const end = new Date(goal.endDate);
            end.setHours(23, 59, 59, 999);
            return start <= fyEnd && end >= fyStart;
        }
        // If only one date, check that one
        if (goal.startDate) {
            const d = new Date(goal.startDate);
            return d >= fyStart && d <= fyEnd;
        }
        if (goal.endDate) {
            const d = new Date(goal.endDate);
            return d >= fyStart && d <= fyEnd;
        }
        // If no dates, include only if in "all" mode – but we're in "current"
        return false;
    });
}, [processedGoals, fiscalYear, selectedFiscalYear, currentFilter]);

  if (isLoading) return (
    <Card style={{ padding: 16 }}>
      <h4>Company Goals</h4>
      <ProgressBar value={50} />
      <div style={{ textAlign: 'center', marginTop: 8 }}>Loading goals and objectives...</div>
    </Card>
  );

  if (error) return (
    <Card style={{ padding: 16, backgroundColor: '#f8d7da' }}>
      <h3>Company Goals</h3>
      <div style={{ color: '#721c24' }}>
        Error loading data: {error.message}
      </div>
    </Card>
  );

  return (
    <Card style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Company Goals ({filteredGoalsByFY.length})</h3>
        {leadershipMetrics?.hasLeadershipData && (
          <Button 
            small
            onClick={() => setShowLeadershipInsights(!showLeadershipInsights)}
          >
            {showLeadershipInsights ? 'Hide' : 'Show'} Leadership Insights
          </Button>
        )}
      </div>

      {/* ENHANCED LEADERSHIP VISIBILITY CARD */}
      {showLeadershipInsights && leadershipMetrics?.hasLeadershipData && (
        <div style={{ 
          marginBottom: 24, 
          padding: 16, 
          backgroundColor: '#f0f7ff', 
          borderRadius: 8,
          border: `1px solid ${colors.blue300}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 20 }}>👥</span>
            <h4 style={{ margin: 0, color: colors.blue800 }}>Leadership & Culture Impact</h4>
            {leadershipMetrics.selfAssessmentComplete ? (
              <span style={{ 
                marginLeft: 'auto', 
                padding: '4px 12px', 
                backgroundColor: colors.green100, 
                color: colors.green800,
                borderRadius: 16,
                fontSize: 12,
                fontWeight: 600
              }}>
                Self-Assessment Complete ✓
              </span>
            ) : (
              <span style={{ 
                marginLeft: 'auto', 
                padding: '4px 12px', 
                backgroundColor: colors.yellow100, 
                color: colors.yellow800,
                borderRadius: 16,
                fontSize: 12,
                fontWeight: 600
              }}>
                Assessment in Progress
              </span>
            )}
          </div>

          {/* Leadership Metrics Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
            gap: 16,
            marginBottom: 16
          }}>
            {/* Average Leadership Score */}
            <Tooltip content="Average score across all leadership competencies">
              <div style={{ 
                padding: 12, 
                backgroundColor: 'white', 
                borderRadius: 6,
                border: `1px solid ${colors.grey300}`
              }}>
                <div style={{ fontSize: 12, color: colors.grey700, marginBottom: 4 }}>Leadership Score</div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: colors.blue700 }}>
                  {leadershipMetrics.averageScore}
                  <span style={{ fontSize: 14, fontWeight: 'normal', color: colors.grey600, marginLeft: 2 }}>/5</span>
                </div>
                <div style={{ fontSize: 12, color: colors.grey600, marginTop: 4 }}>
                  {leadershipMetrics.completionRate}% complete
                </div>
              </div>
            </Tooltip>

            {/* Team Alignment */}
            <Tooltip content="How well leadership scores align with team performance">
              <div style={{ 
                padding: 12, 
                backgroundColor: 'white', 
                borderRadius: 6,
                border: `1px solid ${colors.grey300}`
              }}>
                <div style={{ fontSize: 12, color: colors.grey700, marginBottom: 4 }}>Team Alignment</div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: getProgressColor(leadershipMetrics.teamAlignment) }}>
                  {leadershipMetrics.teamAlignment}%
                </div>
                <ProgressBar value={leadershipMetrics.teamAlignment} height="4px" />
              </div>
            </Tooltip>

            {/* Pending Approvals */}
            <Tooltip content="Number of pending leadership approvals for team members">
              <div style={{ 
                padding: 12, 
                backgroundColor: 'white', 
                borderRadius: 6,
                border: `1px solid ${colors.grey300}`
              }}>
                <div style={{ fontSize: 12, color: colors.grey700, marginBottom: 4 }}>Pending Approvals</div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: leadershipMetrics.pendingApprovals > 0 ? colors.yellow700 : colors.green700 }}>
                  {leadershipMetrics.pendingApprovals}
                </div>
                <div style={{ fontSize: 12, color: colors.grey600 }}>
                  Team: {leadershipMetrics.totalDirectReports} members
                </div>
              </div>
            </Tooltip>
          </div>

          {/* Top Competencies & Areas Needing Attention */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Top Competencies */}
            {leadershipMetrics.topCompetencies.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.grey800, marginBottom: 8 }}>
                  🌟 Top Leadership Strengths
                </div>
                {leadershipMetrics.topCompetencies.map((comp, idx) => (
                  <div key={idx} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 0',
                    borderBottom: idx < leadershipMetrics.topCompetencies.length - 1 ? `1px solid ${colors.grey200}` : 'none'
                  }}>
                    <span style={{ fontSize: 13 }}>{comp.name}</span>
                    <span style={{ 
                      fontSize: 13, 
                      fontWeight: 600,
                      color: comp.score >= 4 ? colors.green700 : colors.blue700
                    }}>
                      {comp.score.toFixed(1)}/5
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Areas Needing Attention */}
            {leadershipMetrics.needsAttention.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.grey800, marginBottom: 8 }}>
                  ⚠️ Development Areas
                </div>
                {leadershipMetrics.needsAttention.slice(0, 3).map((comp, idx) => (
                  <div key={idx} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 0',
                    borderBottom: idx < Math.min(leadershipMetrics.needsAttention.length, 3) - 1 ? `1px solid ${colors.grey200}` : 'none'
                  }}>
                    <span style={{ fontSize: 13 }}>{comp.name}</span>
                    <span style={{ 
                      fontSize: 13, 
                      fontWeight: 600,
                      color: colors.red700
                    }}>
                      {comp.score.toFixed(1)}/5
                    </span>
                  </div>
                ))}
                {leadershipMetrics.needsAttention.length > 3 && (
                  <div style={{ fontSize: 12, color: colors.grey600, marginTop: 4 }}>
                    +{leadershipMetrics.needsAttention.length - 3} more
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Action Buttons */}
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button 
              small
              onClick={() => window.location.href = '#/leadership'}
            >
              Go to Leadership Assessment
            </Button>
            {leadershipMetrics.pendingApprovals > 0 && (
              <Button 
                small
                primary
                onClick={() => window.location.href = '#/leadership?tab=team'}
              >
                Review {leadershipMetrics.pendingApprovals} Pending
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Company-wide Task Completion */}
      {performanceData?.tasks?.events?.length > 0 && (
        <div style={{ marginBottom: 16,
                      padding: '8px 16px',
                      backgroundColor: '#f1f3f5',
                      borderRadius: 6,
                      margin: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, display: 'flex', gap: 4, marginBottom: 10 }}>
            <BiBuildingHouse size={18}/> Company-wide Task Completion: {globalTaskProgress}% ({getProgressDescription(globalTaskProgress)})
          </div>
          <ProgressBar value={globalTaskProgress} />
        </div>
      )}

      {/* Goals List */}
      {filteredGoalsByFY.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: '#6c757d' }}>
          No company goals found in the system.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginLeft: '10px' }}>
          {filteredGoalsByFY.map((goal) => (
            <div
              key={goal.id}
              style={{
                padding: 16,
                border: '1px solid #e9ecef',
                borderRadius: 8,
                backgroundColor: '#f8f5f8ff',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: "0 8px 10px rgba(173, 157, 157, 1)",
                marginBottom: '30px',
                margin: 18,
              }}
            >
              {/* Goal Header */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: '600', fontSize: 16, marginBottom: 8 }}>
                  {goal.name}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 4 }}>
                  <StatusBadge status={goal.status} />
                </div>
                {goal.description && goal.description !== goal.name && (
                  <div style={{ fontSize: 14, color: '#6c757d', lineHeight: 1.4 }}>
                    {goal.description}
                  </div>
                )}
              </div>

              {/* Progress Section */}
              <div style={{ marginBottom: 12, flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>Goal Target</span>
                  </div>
                  <span style={{
                    fontSize: 14,
                    fontWeight: 'bold',
                    color: getProgressColor(goal.progress)
                  }}>
                    {goal.progress}%
                  </span>
                </div>
                <ProgressBar value={goal.progress} />
                <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                  Progress source: {goal.progressSource}
                  {goal.progressSource === 'calculated' && goal.linkedObjectives.length > 0 && (
                    <span> • Based on {goal.linkedObjectives.length} objectives</span>
                  )}
                  {goal.completedTasks > 0 && (
                    <span> • {goal.completedTasks}/{goal.totalTasks} tasks completed</span>
                  )}
                </div>

                {/* Overall Task Progress */}
                {performanceData && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Overall Task Progress</div>
                    <ProgressBar value={goal.overallProgress || 0} />
                    <div style={{ fontSize: 11, color: '#6c757d', marginTop: 2 }}>
                      All tasks: {goal.overallProgress || 0}% ({getProgressDescription(goal.overallProgress || 0)})
                    </div>
                  </div>
                )}
              </div>

              {/* Goal Metadata */}
              <div style={{
                display: 'flex',
                gap: 8,
                fontSize: 12,
                color: '#6c757d',
                flexWrap: 'wrap',
                marginBottom: goal.linkedObjectives.length > 0 ? 12 : 8
              }}>
                <span><strong>Priority:</strong> {goal.priority}</span>
                {goal.category && <span><strong>Category:</strong> {goal.category}</span>}
                {goal.startDate && <span><strong>Start:</strong> {new Date(goal.startDate).toLocaleDateString()}</span>}
                {goal.endDate && <span><strong>End:</strong> {new Date(goal.endDate).toLocaleDateString()}</span>}
                {goal.linkedObjectives.length > 0 && (
                  <span>
                    <strong>Objectives:</strong> {goal.linkedObjectives.length} (avg {goal.calculatedProgress}%)
                  </span>
                )}
              </div>

              {/* Leadership Impact Indicator - New */}
              {leadershipMetrics?.hasLeadershipData && (
                <Tooltip content="This goal's alignment with leadership competencies">
                  <div style={{ 
                    marginTop: 8, 
                    padding: '8px 12px', 
                    backgroundColor: '#f0f2f5', 
                    borderRadius: 4,
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <span>👥</span>
                    <span>Leadership Impact:</span>
                    <span style={{ 
                      fontWeight: 600,
                      color: getProgressColor(leadershipMetrics.teamAlignment)
                    }}>
                      {leadershipMetrics.teamAlignment}%
                    </span>
                  </div>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default RealGoals;
